import styled from 'styled-components';

import rem from 'polished/lib/helpers/rem';
import toColorString from 'polished/lib/color/toColorString';

import color from '@decisiv/design-tokens/lib/color';
import spacing from '@decisiv/design-tokens/lib/spacing';

// Real styled components rather than styled-components' `css` prop: portal's
// babel config has no babel-plugin-styled-components, so a `css={...}` would be
// dropped without warning. Same constraint the rest of this page works under.

/** A step's body: heading block, then the fields, 20px apart as in the mockup. */
export const StepBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(spacing.base)};
`;

/** The heading row: title and subtitle on the left, "Required Fields" opposite. */
export const StepHeading = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${rem(spacing.base)};
`;

export const RequiredMarker = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base / 4)};
  flex-shrink: 0;
  white-space: nowrap;
`;

/** A group of fields with the mockup's 10px rhythm between them. */
export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(spacing.base / 2)};
`;

/** Two fields side by side: Country/State, City/Postal Code. */
export const FieldPair = styled.div`
  display: flex;
  gap: ${rem(spacing.base / 2)};

  > * {
    flex: 1;
    min-width: 0;
  }
`;

/**
 * The country code and the number, plus whatever is said about the pair.
 *
 * The message belongs to the row rather than to the number field: the mockup puts
 * it at x=0 spanning all 590px (Figma 787:76709, 787:76731), and hanging it off
 * the number's `helpMessage` indented it 135px to sit under that column alone.
 */
export const PhoneField = styled.div`
  display: flex;
  flex-direction: column;

  /* What InputContainer gives its own help and warning messages. */
  > * ~ * {
    margin-top: ${rem(spacing.base / 2)};
  }
`;

/**
 * The country code sits in a fixed 130px column beside the number, as in the
 * mockup, rather than sharing the width evenly.
 *
 * 5px, not the 10px the other pairs use: this is the design's one tighter gap
 * (Frame 32 puts the Select at x=0 w=130 and the field at x=135).
 */
export const PhoneRow = styled.div`
  display: flex;
  gap: ${rem(spacing.base / 4)};
`;

export const PhoneCodeColumn = styled.div`
  width: ${rem(130)};
  flex-shrink: 0;
`;

export const PhoneNumberColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

/** The Accounts menu matches its field's width; the field spans the step. */
export const AccountsPanel = styled.div`
  width: ${rem(590)};
  max-width: 100%;
`;

export const AccountsEmpty = styled.div`
  padding: ${rem(spacing.base)};
`;

/**
 * The "loading more" line under the paged options list.
 *
 * Outside the list rather than a row within it: every OptionsList item is
 * selectable, so a status row inside would be tickable.
 */
export const AccountsStatus = styled.div`
  padding: ${rem(spacing.base / 2)} ${rem(spacing.base)};
  border-top: 1px solid ${toColorString(color.opacity.charcoal15)};
`;

/**
 * The Accounts trigger.
 *
 * Cannot be a KeyDS Select — Select is single-select, and Combobox renders no
 * checkboxes when multiple and offers nowhere to hang the mockup's APPLY /
 * CANCEL / CLEAR footer (see AccountsField). But it stands beside real Selects on
 * steps 2 and 3, so its chrome is copied from the library's own field container
 * rather than invented: every value below is InputField/Containers.js's.
 *
 * The 40px height, 2px radius and charcoal40 border it used to carry made the
 * first field the admin sees 10px shorter, rounder and darker than every other
 * field in the wizard, and than the mockup's own 50px Quarter Moon field.
 */
export const AccountsTrigger = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${rem(spacing.base / 2)};
  width: 100%;
  box-sizing: border-box;
  min-height: ${rem(50)};
  /* InputInnerContainer's 6px/10px, and IconContainer's 16px beside the chevron. */
  padding: ${rem(6)} ${rem(spacing.base * 0.8)} ${rem(spacing.base / 2)}
    ${rem(spacing.base / 2)};
  text-align: left;
  cursor: text;
  background: ${toColorString(color.base.snowWhite)};
  border: 1px solid
    ${({ $active }) =>
      toColorString(
        $active ? color.interaction.pacificOcean : color.base.quarterMoon,
      )};
  color: ${toColorString(color.base.charcoal)};

  &:hover {
    border-color: ${toColorString(color.interaction.indianOcean)};
  }
`;

/**
 * The field's own text entry — the mockup filters by typing in the field, not in a
 * search box inside the menu (Figma 787:76816, and 787:76870 for the same pattern
 * on Team). Borderless because AccountsTrigger draws the border.
 */
export const AccountsInput = styled.input`
  flex: 1;
  /* Enough for the placeholder to read whole on its own line once tags wrap above
     it: an input's intrinsic width comes from its size attribute, not its content. */
  min-width: ${rem(180)};
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: ${toColorString(color.base.charcoal)};
  font: inherit;

  &::placeholder {
    color: ${toColorString(color.base.alaskanHusky)};
  }
`;

/** The chevron, which toggles the menu without stealing the input's focus. */
export const AccountsChevron = styled.button`
  display: flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  color: ${toColorString(color.base.alaskanHusky)};
`;

/**
 * The trigger's contents: label above value, which is a KeyDS field's anatomy.
 * Previously the label sat inline with the tags on one row, so it read as another
 * chip rather than as the field's label.
 */
export const AccountsTriggerBody = styled.span`
  display: flex;
  flex-direction: column;
  gap: ${rem(spacing.base / 4)};
  /* flex: 1 so it fills the field. Without it this is a flex item of a
     space-between row, so it shrinks to the width of the short "Account" label and
     leaves the text entry at its ~20-character intrinsic width — which clipped the
     placeholder mid-word. */
  flex: 1;
  min-width: 0;
`;

/**
 * Selected accounts as tags beneath the label, with the text entry sharing the
 * row and wrapping past them when there are many.
 */
export const AccountsTags = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${rem(spacing.base / 4)};
  width: 100%;
  min-width: 0;
`;

/** Matches InputLabel: P as a label, alaskanHusky, small, its own block. */
export const AccountsLabel = styled.span`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base / 4)};
`;

/** A labelled section on the Permissions step. */
export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(spacing.base / 2)};
`;

/** A section heading that carries the required marker, as the field labels do. */
export const SectionHeading = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base / 4)};
`;

export const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(spacing.base / 2)};
`;

/** The Member/Manager radios, indented under the Management Role checkbox. */
export const NestedChoices = styled.div`
  margin-left: ${rem(spacing.base * 1.5)};
`;

/** The terminal panels are centred in the step body, as in the mockup. */
export const TerminalPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(spacing.base)};
  padding: ${rem(spacing.base)} 0;
  text-align: center;
`;

export const TerminalActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(spacing.base / 2)};
`;

/**
 * The terminal phases' footer buttons.
 *
 * Wizard's Footer is `flex-direction: row-reverse; justify-content: space-between`
 * and its own `defaultActions` wraps the step buttons in an ActionButtonsContainer
 * for exactly that reason. Returning bare Buttons from `renderActions` instead
 * makes each one a direct flex child, so two of them land at *opposite ends* of
 * the footer, reversed — which is what put the error panel's CLOSE on the far left
 * and BACK on the far right.
 *
 * One wrapper is one flex item, so `space-between` puts it at main-start, which
 * `row-reverse` makes the right edge. Inside, a normal row renders DOM order
 * left to right. 10px matches ActionButtonsContainer's own column-gap.
 *
 * Not imported from the library's Wizard/styles: reaching into a component's
 * internal styled-components would break on any KeyDS bump.
 */
export const FooterActions = styled.div`
  display: flex;
  gap: ${rem(spacing.base / 2)};
`;

/** The creating panel is a single left-aligned row, unlike the other two. */
export const CreatingRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(spacing.base / 2)};
`;
